import React, { useEffect, useState, useCallback } from 'react';
import { Building2, Users, TrendingUp, CheckCircle, UserPlus, BarChart3, PieChart as PieIcon, Activity, UsersRound } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';
import { organizationsService } from '../services/organizations';
import { enrollmentsService } from '../services/enrollments';
import { Organization, OrganizationWithStats } from '../types';
import { supabase } from '../config/supabase';
import { PeriodSelector, PeriodType, DateRange, getDateRangeForPeriod } from '../components/Dashboard/PeriodSelector';

// ── Design tokens (Ondako Playful Home) ──
const CORAL  = '#af3216';
const PEACH  = '#ffac9a';
const MINT   = '#2f9e5a';
const GOLD   = '#f39200';
const MUTED  = '#b7b1ab';

interface AdminStats {
  organizationsCount: number;
  activeOrganizationsCount: number;
  learnersCount: number;
  activeLearnersCount: number;
  newLearnersCount: number;
  completedInPeriodCount: number;
  overallCompletionRate: number;
}

interface MonthlyPoint { month: string; inscriptions: number; completions: number }
interface StatusPoint { name: string; value: number; color: string }
interface TopCoursePoint { name: string; inscrits: number; fill: string }
interface GroupPerfPoint { name: string; inscriptions: number; terminees: number }

export const DashboardAdmin: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationWithStats[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('30days');
  const [dateRange, setDateRange] = useState<DateRange>(() => getDateRangeForPeriod('30days'));
  const [periodLabel, setPeriodLabel] = useState<string>('30 derniers jours');

  const [monthlyData, setMonthlyData] = useState<MonthlyPoint[]>([]);
  const [statusData, setStatusData] = useState<StatusPoint[]>([]);
  const [topCourses, setTopCourses] = useState<TopCoursePoint[]>([]);
  const [groupPerf, setGroupPerf] = useState<GroupPerfPoint[]>([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  const handlePeriodChange = useCallback((period: PeriodType, range: DateRange) => {
    setSelectedPeriod(period);
    setDateRange(range);
    const labels: Record<PeriodType, string> = {
      '7days': '7 derniers jours',
      '30days': '30 derniers jours',
      '1year': '1 an',
      'custom': 'Période sélectionnée',
    };
    setPeriodLabel(labels[period]);
  }, []);

  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const orgs = await organizationsService.getAll();
        setAllOrganizations(orgs);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };
    loadOrgs();
  }, []);

  useEffect(() => {
    loadDashboardData(dateRange, selectedOrgId);
    loadChartsData(selectedOrgId);
  }, [dateRange, selectedOrgId]);

  const loadChartsData = async (orgId: string) => {
    try {
      setChartsLoading(true);
      const orgParam = orgId || null;

      const [monthly, statusDist, top, groupRates] = await Promise.all([
        enrollmentsService.getMonthlyStats(orgParam, 7),
        enrollmentsService.getOrganizationStats(orgParam),
        enrollmentsService.getTopCourses(orgParam, 5),
        enrollmentsService.getGroupCompletionRates(orgParam),
      ]);

      setMonthlyData(
        monthly.map((m: { monthKey: string; inscriptions: number; completions: number }) => ({
          month: m.monthKey,
          inscriptions: m.inscriptions,
          completions: m.completions,
        }))
      );

      const total = statusDist.totalEnrollments;
      if (total > 0) {
        setStatusData([
          { name: 'Terminés',       value: statusDist.completedEnrollments,   color: MINT  },
          { name: 'En cours',       value: statusDist.inProgressEnrollments,  color: GOLD  },
          { name: 'Non commencés',  value: statusDist.notStartedEnrollments,  color: MUTED },
        ]);
      } else {
        setStatusData([]);
      }

      const barColors = [CORAL, PEACH, '#cf6847', '#df8462', '#efa07d'];
      setTopCourses(
        top.map((c: { courseName: string; inscrits: number }, i: number) => ({
          name: c.courseName.length > 28 ? c.courseName.slice(0, 28) + '...' : c.courseName,
          inscrits: c.inscrits,
          fill: barColors[i] || PEACH,
        }))
      );

      setGroupPerf(
        groupRates.slice(0, 8).map((g: { groupName: string; totalEnrollments: number; completedEnrollments: number }) => ({
          name: g.groupName.length > 22 ? g.groupName.slice(0, 22) + '...' : g.groupName,
          inscriptions: g.totalEnrollments,
          terminees: g.completedEnrollments,
        }))
      );
    } catch (error) {
      console.error('Error loading charts:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  const loadDashboardData = async (range: DateRange, orgId: string) => {
    try {
      setLoading(true);

      const orgsWithStats = await organizationsService.getAllWithStats();
      setOrganizations(orgId ? orgsWithStats.filter(o => o.id === orgId) : orgsWithStats);

      let learnersQuery = supabase
        .from('learners')
        .select('status, created_at, organization_id');
      if (orgId) {
        learnersQuery = learnersQuery.eq('organization_id', orgId);
      }
      const { data: allLearners } = await learnersQuery;

      const activeOrgsCount = orgId
        ? (orgsWithStats.find(o => o.id === orgId)?.status === 'active' ? 1 : 0)
        : orgsWithStats.filter(o => o.status === 'active').length;

      const enrollmentStats = orgId
        ? await enrollmentsService.getOrganizationStats(orgId)
        : null;

      const learners = allLearners || [];
      const totalLearners = learners.length;
      const activeLearners = learners.filter(l => l.status === 'active').length;

      const startTime = range.startDate.getTime();
      const endTime = range.endDate.getTime();

      const newLearnersInPeriod = learners.filter(l => {
        if (!l.created_at) return false;
        const t = new Date(l.created_at).getTime();
        return t >= startTime && t <= endTime;
      }).length;

      const monthlyStats = await enrollmentsService.getMonthlyStats(orgId || null);
      const completedInPeriod = monthlyStats.reduce((sum: number, m: { completions: number }) => sum + m.completions, 0);

      const completionRate = enrollmentStats
        ? (enrollmentStats.totalEnrollments > 0
          ? Math.round((enrollmentStats.completedEnrollments / enrollmentStats.totalEnrollments) * 100)
          : 0)
        : (totalLearners > 0
          ? Math.round((learners.filter(l => l.status === 'completed' || l.status === 'inactive').length / totalLearners) * 100)
          : 0);

      setStats({
        organizationsCount: orgId ? 1 : orgsWithStats.length,
        activeOrganizationsCount: activeOrgsCount,
        learnersCount: totalLearners,
        activeLearnersCount: activeLearners,
        newLearnersCount: newLearnersInPeriod,
        completedInPeriodCount: completedInPeriod,
        overallCompletionRate: completionRate,
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">Tableau de bord</h1>
          <p className="text-ink-soft mt-1">
            {selectedOrgId
              ? `Stats pour ${allOrganizations.find(o => o.id === selectedOrgId)?.name || 'Organisation'}`
              : "Vue d'ensemble d'Ondako Manager"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-ink-soft" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="input w-auto"
            >
              <option value="">Toutes les organisations</option>
              {allOrganizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
          <PeriodSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} currentRange={dateRange} />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Users}       label="Apprenants"          value={stats?.learnersCount || 0}
          bgColor="#e7deff" iconColor="#634ac1" subtitle={`${stats?.activeLearnersCount || 0} en formation`} />
        <KPICard icon={UserPlus}    label="Nouveaux apprenants" value={stats?.newLearnersCount || 0}
          bgColor="#ffac9a" iconColor="#af3216" subtitle={periodLabel} />
        <KPICard icon={CheckCircle} label="Formations terminées" value={stats?.completedInPeriodCount || 0}
          bgColor="#c8efd4" iconColor="#2f9e5a" subtitle={periodLabel} />
        <KPICard icon={TrendingUp}  label="Taux de complétion"  value={`${stats?.overallCompletionRate || 0}%`}
          bgColor="#fdd34d" iconColor="#745c00" subtitle="global" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Evolution — AreaChart */}
        <div className="lg:col-span-2 bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Activity size={20} style={{ color: CORAL }} />
            <h2 className="text-lg font-semibold text-ink">Évolution des inscriptions</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : monthlyData.length === 0 ? (
            <ChartEmpty label="Aucune donnée pour cette période" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="gradCoral" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CORAL} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={CORAL} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradPeach" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PEACH} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={PEACH} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee7e2" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="inscriptions" name="Inscriptions" stroke={CORAL} fill="url(#gradCoral)"
                  strokeWidth={2} dot={{ r: 4, fill: CORAL, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                <Area type="monotone" dataKey="completions" name="Complétions" stroke={PEACH} fill="url(#gradPeach)"
                  strokeWidth={2} dot={{ r: 4, fill: PEACH, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
          {!chartsLoading && monthlyData.length > 0 && (
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CORAL }} />
                <span className="text-xs text-ink-soft">Inscriptions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PEACH }} />
                <span className="text-xs text-ink-soft">Complétions</span>
              </div>
            </div>
          )}
        </div>

        {/* Repartition — Donut */}
        <div className="bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <PieIcon size={20} style={{ color: MINT }} />
            <h2 className="text-lg font-semibold text-ink">Répartition</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : statusData.length === 0 ? (
            <ChartEmpty label="Aucune inscription" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85} paddingAngle={3}
                    dataKey="value" strokeWidth={0}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-col gap-2 mt-2">
                {statusData.map((s) => (
                  <div key={s.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-xs text-ink-soft">{s.name}</span>
                    </div>
                    <span className="text-xs font-medium text-ink">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Courses — BarChart */}
        <div className="bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 size={20} style={{ color: CORAL }} />
            <h2 className="text-lg font-semibold text-ink">Top formations</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : topCourses.length === 0 ? (
            <ChartEmpty label="Aucune formation inscrite" />
          ) : (
            <ResponsiveContainer width="100%" height={topCourses.length * 52 + 20}>
              <BarChart data={topCourses} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee7e2" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                  tick={{ fontSize: 12, fill: MUTED }} width={160} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="inscrits" name="Inscrits" radius={[0, 6, 6, 0]} barSize={28}>
                  {topCourses.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Group Performance — Stacked BarChart */}
        <div className="bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <UsersRound size={20} style={{ color: MINT }} />
            <h2 className="text-lg font-semibold text-ink">Performances des groupes</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : groupPerf.length === 0 ? (
            <ChartEmpty label="Aucun groupe avec des inscriptions" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={groupPerf.length * 52 + 20}>
                <BarChart data={groupPerf} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee7e2" horizontal={false} />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                    tick={{ fontSize: 12, fill: MUTED }} width={140} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="inscriptions" name="Inscriptions" stackId="a" fill={CORAL} radius={[0, 0, 0, 0]} barSize={28} />
                  <Bar dataKey="terminees"    name="Terminées"    stackId="a" fill={PEACH} radius={[0, 6, 6, 0]} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CORAL }} />
                  <span className="text-xs text-ink-soft">Inscriptions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PEACH }} />
                  <span className="text-xs text-ink-soft">Terminées</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white shadow-ambient rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-cream-high">
          <h2 className="text-lg font-semibold text-ink">
            {selectedOrgId ? 'Organisation sélectionnée' : 'Organisations'}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream-high/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Organisation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Apprenants</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Taux</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-high">
              {organizations.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-ink-soft">
                    Aucune organisation pour le moment
                  </td>
                </tr>
              ) : (
                organizations.map((org) => (
                  <tr key={org.id} className="hover:bg-cream-high/30">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {org.logoUrl ? (
                          <img src={org.logoUrl} alt={org.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-lavender-container flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-lavender" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-ink">{org.name}</div>
                          {org.contactEmail && (
                            <div className="text-sm text-ink-soft">{org.contactEmail}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={org.status} />
                    </td>
                    <td className="px-6 py-4 text-ink font-medium">{org.learnersCount}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-3 bg-cream-high rounded-full max-w-[100px] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${org.completionRate}%`,
                              backgroundColor: org.completionRate >= 75 ? MINT : org.completionRate >= 50 ? GOLD : '#EF4444',
                            }}
                          />
                        </div>
                        <span className="text-sm text-ink-soft">{org.completionRate}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Shared sub-components ──

interface KPICardProps {
  icon: React.FC<{ className?: string; size?: string | number; style?: React.CSSProperties }>;
  label: string;
  value: string | number;
  bgColor: string;
  iconColor: string;
  subtitle?: string;
}

const KPICard: React.FC<KPICardProps> = ({ icon: Icon, label, value, bgColor, iconColor, subtitle }) => (
  <div className="bg-white shadow-ambient rounded-2xl p-5 hover:shadow-ambient-lg transition-shadow duration-200">
    <div className="flex items-center gap-3.5">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgColor }}>
        <Icon size={22} style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-ink leading-none">{value}</p>
        <p className="text-xs text-ink-soft mt-1 truncate">{label}</p>
        {subtitle && <p className="text-xs text-ink-soft/70 truncate">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    active:   'bg-mint-container text-mint',
    inactive: 'bg-cream-high text-ink-soft',
  };
  const labels: Record<string, string> = {
    active:   'Actif',
    inactive: 'Inactif',
  };
  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.inactive}`}>
      {labels[status] || status}
    </span>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-ambient rounded-xl px-4 py-3">
      {label && <p className="text-xs font-medium text-ink mb-1">{label}</p>}
      {payload.map((p: { name: string; value: number; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-xs text-ink-soft">{p.name} :</span>
          <span className="text-xs font-medium text-ink">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartSkeleton: React.FC = () => (
  <div className="flex items-center justify-center bg-cream-high/30 rounded-xl animate-pulse" style={{ height: 220 }}>
    <div
      className="animate-spin rounded-full h-8 w-8 border-2 border-transparent"
      style={{ borderTopColor: CORAL, borderRightColor: PEACH }}
    />
  </div>
);

const ChartEmpty: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center text-sm text-ink-soft" style={{ height: 220 }}>
    {label}
  </div>
);
